import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost, getMessages, getConversationMessages } from '../../utils/api';

// Async thunk to fetch conversations from external API
// getMessages API returns conversations/threads list
export const fetchConversations = createAsyncThunk(
    'messages/fetchConversations',
    async ({ search = '', limit = 20, page = 1 } = {}, { rejectWithValue }) => {
        try {
            // Call external Node.js API: https://octopusworkapp.com:9001/messages/getMessages
            // This returns list of conversations/message threads
            const response = await getMessages({ search, limit, page });
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch conversations');
        }
    }
);

// Async thunk to fetch messages for a specific conversation
export const fetchConversationMessages = createAsyncThunk(
    'messages/fetchConversationMessages',
    async ({ conversationId, limit = 50, page = 1 }, { rejectWithValue }) => {
        try {
            const response = await getConversationMessages(conversationId, { limit, page });
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch conversation messages');
        }
    }
);

// Async thunk to send a message
export const sendMessage = createAsyncThunk(
    'messages/sendMessage',
    async ({ conversationId, message }, { rejectWithValue }) => {
        try {
            // Replace with your actual API endpoint
            const sentMessage = await apiPost('messages', {
                conversation_id: conversationId,
                message: message,
            });
            return sentMessage;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to send message');
        }
    }
);

const messagesSlice = createSlice({
    name: 'messages',
    initialState: {
        // Conversations from external API (getMessages returns conversations)
        conversations: [],
        conversationsPagination: {
            page: 1,
            limit: 20,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false,
        },
        
        // Messages for active conversation
        messages: [],
        messagesPagination: {
            page: 1,
            limit: 50,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false,
        },
        
        // Sample conversations (fallback if API not available)
        sampleConversations: [
            {
                id: 1,
                name: 'Craftsman - Management',
                avatar: '🐝',
                lastMessage: 'Jack Ronie: Thursday 30...',
                timestamp: 'Friday',
                unread: false,
            },
            {
                id: 2,
                name: 'Ivy Lodge -Management',
                avatar: '🍃',
                lastMessage: 'Chris Reid: Terrace 200.9...',
                timestamp: 'Thursday',
                unread: false,
            },
            {
                id: 3,
                name: 'Malones ABZ - Management',
                avatar: '🏢',
                lastMessage: 'Sam Cairney: "Malones b...',
                timestamp: 'Oct 28',
                unread: true,
            },
            {
                id: 4,
                name: 'SV - Pre Purchase Notification...',
                avatar: '🐷',
                lastMessage: 'Nat Jankowska: sain...',
                timestamp: 'Oct 28',
                unread: true,
            },
            {
                id: 5,
                name: 'Shiprow Village - Management',
                avatar: '👥',
                lastMessage: 'Millie Hordowicz: lo...',
                timestamp: 'Oct 9',
                unread: true,
            },
            {
                id: 6,
                name: 'Testing Group',
                avatar: '👤',
                lastMessage: 'You: we\'ve',
                timestamp: 'Sep 29',
                unread: false,
            },
            {
                id: 7,
                name: 'New App Testing/Feedback Gr...',
                avatar: '👤',
                lastMessage: 'robby_user user: Test',
                timestamp: 'Sep 29',
                unread: true,
            },
        ],
        activeConversation: 1,
        searchQuery: '',
        messageInput: '',
        isLoading: false,
        error: null,
        sendingMessage: false,
    },
    reducers: {
        setActiveConversation: (state, action) => {
            state.activeConversation = action.payload;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        setMessageInput: (state, action) => {
            state.messageInput = action.payload;
        },
        addMessage: (state, action) => {
            state.messages.push(action.payload);
        },
        clearMessageInput: (state) => {
            state.messageInput = '';
        },
        markConversationAsRead: (state, action) => {
            const conversation = state.conversations.find(c => c.id === action.payload);
            if (conversation) {
                conversation.unread = false;
            }
        },
        updateConversations: (state, action) => {
            state.conversations = action.payload;
        },
        updateMessages: (state, action) => {
            state.messages = action.payload;
        },
        setConversationsPagination: (state, action) => {
            state.conversationsPagination = action.payload;
        },
        setConversationsPage: (state, action) => {
            state.conversationsPagination.page = action.payload;
        },
        setConversationsLimit: (state, action) => {
            state.conversationsPagination.limit = action.payload;
        },
        setMessagesPagination: (state, action) => {
            state.messagesPagination = action.payload;
        },
        setMessagesPage: (state, action) => {
            state.messagesPagination.page = action.payload;
        },
        setMessagesLimit: (state, action) => {
            state.messagesPagination.limit = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Conversations from external API (getMessages returns conversations)
            .addCase(fetchConversations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchConversations.fulfilled, (state, action) => {
                state.isLoading = false;
                
                // Handle response from external API
                if (action.payload.data) {
                    state.conversations = action.payload.data;
                } else if (Array.isArray(action.payload)) {
                    state.conversations = action.payload;
                } else {
                    state.conversations = [];
                }
                
                // Update pagination if provided
                if (action.payload.pagination) {
                    state.conversationsPagination = {
                        page: action.payload.pagination.page || 1,
                        limit: action.payload.pagination.limit || 20,
                        total: action.payload.pagination.total || 0,
                        total_pages: action.payload.pagination.total_pages || 0,
                        has_next: action.payload.pagination.has_next || false,
                        has_prev: action.payload.pagination.has_prev || false,
                    };
                }
                
                // Set first conversation as active if none selected
                if (state.conversations.length > 0 && !state.activeConversation) {
                    state.activeConversation = state.conversations[0].id;
                }
            })
            .addCase(fetchConversations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                // Use sample conversations as fallback
                state.conversations = state.sampleConversations;
            })
            // Fetch Messages for specific conversation
            .addCase(fetchConversationMessages.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchConversationMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                
                // Handle response from external API
                if (action.payload.data) {
                    state.messages = action.payload.data;
                } else if (Array.isArray(action.payload)) {
                    state.messages = action.payload;
                } else {
                    state.messages = [];
                }
                
                // Update messages pagination if provided
                if (action.payload.pagination) {
                    state.messagesPagination = {
                        page: action.payload.pagination.page || 1,
                        limit: action.payload.pagination.limit || 50,
                        total: action.payload.pagination.total || 0,
                        total_pages: action.payload.pagination.total_pages || 0,
                        has_next: action.payload.pagination.has_next || false,
                        has_prev: action.payload.pagination.has_prev || false,
                    };
                }
            })
            .addCase(fetchConversationMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Send Message
            .addCase(sendMessage.pending, (state) => {
                state.sendingMessage = true;
                state.error = null;
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                state.sendingMessage = false;
                state.messages.push(action.payload);
                state.messageInput = '';
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.sendingMessage = false;
                state.error = action.payload;
            });
    },
});

// Export sync actions
export const {
    setActiveConversation,
    setSearchQuery,
    setMessageInput,
    addMessage,
    clearMessageInput,
    markConversationAsRead,
    updateConversations,
    updateMessages,
    setConversationsPagination,
    setConversationsPage,
    setConversationsLimit,
    setMessagesPagination,
    setMessagesPage,
    setMessagesLimit,
} = messagesSlice.actions;

export default messagesSlice.reducer;

